import { useRef, useState } from "react";
import { api, inr } from "../lib/api";
import { toast } from "sonner";
import { UploadSimple, X } from "@phosphor-icons/react";

export function BankImport({ q, parties, onDone }) {
  const [rows, setRows] = useState(null);
  const [openDocs, setOpenDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const ce = q ? decodeURIComponent(q.split("=")[1]) : undefined;

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/bookkeeping-import/preview${q}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(data.rows.map((r) => ({ ...r, include: !r.duplicate })));
      setOpenDocs(data.open_docs || []);
      toast.success(`Found ${data.count} transaction(s)${data.matched ? ` · ${data.matched} matched to invoices/bills` : ""}${data.duplicates ? ` · ${data.duplicates} possible duplicate(s) unticked` : ""}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read statement"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const upd = (i, k, v) => setRows((p) => p.map((r, j) => {
    if (j !== i) return r;
    const n = { ...r, [k]: v };
    if (k === "txn_type") n.invoice_id = "";
    if (k === "invoice_id" && v) { const o = openDocs.find((x) => x.id === v); if (o) n.party = o.party; }
    return n;
  }));
  const commit = async () => {
    const sel = rows.filter((r) => r.include);
    if (!sel.length) { toast.error("Select at least one transaction"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookkeeping-import/commit", { client_email: ce, rows: sel.map(({ include, duplicate, ...r }) => r) });
      toast.success(`Imported ${data.imported} bank entr${data.imported === 1 ? "y" : "ies"}${data.matched ? ` · ${data.matched} invoice/bill payment(s) reconciled` : ""}`);
      setRows(null); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  const picked = rows ? rows.filter((r) => r.include) : [];

  return (
    <div className="border border-dashed border-[#0F766E]/40 rounded-sm p-4 mb-5" data-testid="bk-import">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-semibold">Import bank statement</p>
          <p className="text-xs text-[#888]">Excel (.xlsx) or CSV with Date, Narration and Withdrawal/Deposit (or Amount) columns.</p>
        </div>
        <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv" hidden data-testid="bk-import-file" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        <button data-testid="bk-import-btn" disabled={busy} onClick={() => ref.current?.click()} className="bg-[#0F766E] hover:brightness-95 text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-50">
          <UploadSimple size={15} /> {busy ? "Reading…" : "Upload statement"}
        </button>
      </div>
      {rows && (
        <div className="mt-4" data-testid="bk-import-preview">
          <datalist id="dl-import-parties">{parties.map((p) => <option key={p.id} value={p.name} />)}</datalist>
          <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="sticky top-0 bg-white"><tr className="label text-left border-b border-black/10">
                <th className="pb-2 w-8"><input type="checkbox" data-testid="bk-import-all" checked={picked.length === rows.length} onChange={(e) => setRows(rows.map((r) => ({ ...r, include: e.target.checked })))} /></th>
                <th className="pb-2">Date</th><th className="pb-2">Narration</th><th className="pb-2">Type</th><th className="pb-2">Party</th><th className="pb-2">Reconcile with</th><th className="pb-2 text-right">Amount</th></tr></thead>
              <tbody>{rows.map((r, i) => (
                <tr key={i} data-testid={`bk-import-row-${i}`} className={`border-b border-black/5 ${r.include ? "" : "opacity-50"}`}>
                  <td className="py-1.5"><input type="checkbox" data-testid={`bk-import-inc-${i}`} checked={r.include} onChange={(e) => upd(i, "include", e.target.checked)} /></td>
                  <td className="tnum whitespace-nowrap pr-2">{r.date}</td>
                  <td className="pr-2 max-w-[280px] truncate" title={r.description}>{r.description || "—"}{r.duplicate && <span className="ml-2 text-[10px] text-[#8a6d00] bg-[#F5A623]/15 px-1.5 py-0.5 rounded-sm">possible duplicate</span>}</td>
                  <td className="pr-2"><select data-testid={`bk-import-type-${i}`} value={r.txn_type} onChange={(e) => upd(i, "txn_type", e.target.value)} className="border border-black/20 rounded-sm px-1 py-0.5 text-xs"><option>Receipt</option><option>Payment</option></select></td>
                  <td className="pr-2"><input data-testid={`bk-import-party-${i}`} list="dl-import-parties" value={r.party} onChange={(e) => upd(i, "party", e.target.value)} className="border border-black/20 rounded-sm px-1.5 py-0.5 text-xs w-32" /></td>
                  <td className="pr-2"><select data-testid={`bk-import-match-${i}`} value={r.invoice_id || ""} onChange={(e) => upd(i, "invoice_id", e.target.value)}
                    className={`border rounded-sm px-1 py-0.5 text-xs max-w-[200px] ${r.invoice_id ? "border-[#00A86B] bg-[#00A86B]/5 text-[#00786f]" : "border-black/20"}`}>
                    <option value="">— Not linked —</option>
                    {openDocs.filter((o) => o.kind === (r.txn_type === "Receipt" ? "sales" : "purchases")).map((o) => <option key={o.id} value={o.id}>{o.invoice_no || "Bill"} · {o.party} · {inr(o.outstanding)}</option>)}
                  </select></td>
                  <td className={`text-right tnum ${r.txn_type === "Receipt" ? "text-[#00A86B]" : "text-[#DC2626]"}`}>{inr(r.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3 mt-3">
            <span className="text-xs text-[#555]">{picked.length} selected · {picked.filter((r) => r.invoice_id).length} reconciled · In {inr(picked.filter((r) => r.txn_type === "Receipt").reduce((a, r) => a + r.amount, 0))} · Out {inr(picked.filter((r) => r.txn_type === "Payment").reduce((a, r) => a + r.amount, 0))}</span>
            <button data-testid="bk-import-cancel" onClick={() => setRows(null)} className="border border-black/20 hover:bg-black/5 px-3 py-2 rounded-sm text-sm inline-flex items-center gap-1"><X size={14} /> Cancel</button>
            <button data-testid="bk-import-commit" disabled={busy} onClick={commit} className="bg-[#0F766E] hover:brightness-95 text-white px-4 py-2 rounded-sm text-sm disabled:opacity-50">Import {picked.length} entr{picked.length === 1 ? "y" : "ies"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
